import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import { Button, Icon } from '@ui-library/core'
import { LoaderRegular } from '@ui-library/icons'

import AutocompleteMultiple from '../../../Autocomplete/AutocompleteMultiple/AutocompleteMultiple'
import ProgressBar from '../../../Bars/ProgressBar/ProgressBar'
import Select from '../../../Select/Select'

import {
  masterDisplayDataSelector,
  masterDisplayListToDisplaySelector,
  masterDisplayLoadingSelector,
  masterDisplayRadiusSelector,
  masterDisplayResSelector,
  setRadius,
} from '../../../../redux/reducers/MasterDisplay/masterDisplay.reducer'

import { useProductMasterDisplayHeader } from '../../../../hooks/products/masterDisplay/useProductMasterDisplayHeader'

import { productNamesType } from '../../../../types/productNames.types'
import { SelectElementType } from '../../../../types/select.type'

const MasterDisplayHeader = (): React.ReactElement => {
  const dispatch = useDispatch()
  const data = useSelector(masterDisplayDataSelector)
  const res = useSelector(masterDisplayResSelector)
  const loading = useSelector(masterDisplayLoadingSelector)
  const listToDisplay = useSelector(masterDisplayListToDisplaySelector)
  const radius = useSelector(masterDisplayRadiusSelector)

  const {
    errorBroadcastMediums,
    errorProjectTypes,
    showErrorLocalities,
    handleBroadcastMediums,
    handleChangeLocations,
    handleProjectTypes,
    handleProcess,
  } = useProductMasterDisplayHeader()

  const broadcastMediumsChecked =
    data?.broadcastMediums?.filter((b: SelectElementType) => b.checked)
      ?.length > 0
      ? data?.broadcastMediums?.filter((b: SelectElementType) => b.checked)
      : null
  const projectTypesChecked =
    data?.projectTypes?.filter((b: SelectElementType) => b.checked)?.length > 0
      ? data.projectTypes.filter((b: SelectElementType) => b.checked)
      : null

  return (
    <MasterDisplayHeaderWrapper data-testid="masterDisplayHeader">
      <ProgressBar />
      <MasterDisplayHeaderContainer>
        <h2>
          Configuration du comptage{' '}
          <span>{productNamesType.MasterDisplay}</span>
        </h2>
        <FormWrapper>
          <FormContainer>
            <LocalitiesDiv data-testid="localitiesSelect">
              <AutocompleteMultiple
                catchmentZone={data?.catchmentZone ?? undefined}
                completingWord="localités"
                data-testid="AutocompleteMasterDisplay"
                disabled={loading === 'pending'}
                error={showErrorLocalities}
                initDataLocality={listToDisplay ?? data?.locations ?? undefined}
                label="Localité(s) recherchée(s) par l'internaute"
                maximumItems={50}
                noResultFound="Aucun résultat ne correspond à votre recherche"
                otherSuggestionsText="Un autre lieu ?"
                placeholder="Ajouter une localité (ville, quartier, département...)"
                textError="Sélectionnez au moins une localité"
                titleList="Localité(s)"
                autocompleteCallback={(locations) =>
                  handleChangeLocations(locations)
                }
                radius={radius}
                persistRadius={(value: number) =>
                  dispatch(setRadius({ radius: value }))
                }
              />
            </LocalitiesDiv>
            <BroadcastMediumDiv>
              <Select
                disabled={loading === 'pending'}
                id="broadcastMedium"
                label="Support de diffusion"
                options={data?.broadcastMediums}
                msgError="Sélectionnez au moins une valeur"
                multiple
                placeholder="Ex: Se Loger"
                showError={errorBroadcastMediums}
                value={broadcastMediumsChecked}
                onChange={handleBroadcastMediums}
              />
            </BroadcastMediumDiv>
            <ProjectTypeDiv>
              <Select
                disabled={loading === 'pending'}
                id="projectType"
                options={data?.projectTypes}
                label="Type de projet"
                msgError="Sélectionnez au moins une valeur"
                multiple
                placeholder="Ex: Achat"
                showError={errorProjectTypes}
                value={projectTypesChecked}
                onChange={handleProjectTypes}
              />
            </ProjectTypeDiv>
            <PeriodicityDiv>
              <Select
                disabled
                label="Périodicité"
                msgError="Sélectionnez au moins une valeur"
                off
                options={data?.periodicity}
                placeholder="Ex: Récurrent"
                value={data?.periodicity}
                onChange={() => true}
              />
            </PeriodicityDiv>
            <EstimateButton>
              <Button
                tag="button"
                size="medium"
                kind="primary"
                variant="normal"
                onClick={handleProcess}
                disabled={res}
              >
                {loading === 'pending' ? (
                  <Icon icon={LoaderRegular} style={{ display: 'flex' }} />
                ) : (
                  'Estimer'
                )}
              </Button>
            </EstimateButton>
          </FormContainer>
        </FormWrapper>
      </MasterDisplayHeaderContainer>
    </MasterDisplayHeaderWrapper>
  )
}

const MasterDisplayHeaderWrapper = styled.div`
  position: absolute;
  top: 0;
  z-index: ${(props) => props.theme.global.myAdFactory.zIndex.form};
  height: 14em;
  width: 100%;
  background-color: ${(props) => props.theme.global.colorPalette.lighterSaphir};
`

const MasterDisplayHeaderContainer = styled.div`
  margin: 2.5em 2.5em 0 2.5em;

  h2 {
    font-size: 1.15em;
    font-weight: 700;

    span {
      font-weight: normal;
    }
  }
`

const FormWrapper = styled.div`
  position: relative;
  height: 9.5em;
  margin-top: 1em;
  width: 100%;
  background-color: ${(props) => props.theme.global.colorPalette.white};
  border-radius: 0.3em;
  padding: 1em 1.5em 2.75em 1.5em;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.15);
`

const FormContainer = styled.div`
  display: flex;
  flex: 0 0 100%;
`

const LocalitiesDiv = styled.div`
  width: 34%;
  margin-right: 2%;
`

const BroadcastMediumDiv = styled.div`
  width: 20%;
  margin-right: 2%;
`

const ProjectTypeDiv = styled.div`
  width: 20%;
  margin-right: 2%;
`

const PeriodicityDiv = styled.div`
  width: 20%;
`

const EstimateButton = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  margin: auto;
  width: 10.75em;
  bottom: 0;
  transform: translateY(50%);

  button {
    width: 100%;
  }
`

export default MasterDisplayHeader
